import { useCallback, useMemo, useRef, useContext } from 'react';
import { FlowBuilderContext } from '../context';
import debounce from 'lodash/debounce';
import {
  UploadNode,
  AdditionalVariableNode,
  TransformAddNode,
  StepNode,
} from './nodes';
import { smartConnectorToNodes } from './layout';
import { useTranslateExtent } from 'pages/AutomationEngine/Engine/Flow/hooks';
import { ReactFlow } from 'pages/AutomationEngine/Engine/Flow/styles';
import { StyledFlowWrapper } from './styles';
import { BackgroundCanvas } from './BackgroundCanvas';
import { useScrollFlowWhilePlacing } from '../hooks/useScrollFlowWhilePlacing';
import { BasicEdge } from './edges';
import { useFlowLayout } from 'pages/AutomationEngine/Engine/Flow/useFlowLayout';

const MIN_ZOOM = 0.7;
const MAX_ZOOM = 1.3;
const CANVAS_PADDING = 75;
const CANVAS_TOP_PADDING = 100;

const nodesAndEdges = {
  nodeTypes: {
    connection_run_get_requests: StepNode,
    connection_initialize_additional_variables: StepNode,
    automationStep: StepNode,
    automationTrigger: StepNode,
    upload: UploadNode,
    additionalVariables: AdditionalVariableNode,
    transformAdd: TransformAddNode,
  },
  edgeTypes: {
    basic: BasicEdge,
  },
};

export const Flow = () => {
  const {
    setInteracting,
    steps,
    triggers,
    connections,
    uploads,
    lastPlacingStep,
  } = useContext(FlowBuilderContext);

  const [nodes, edges] = useMemo(
    () =>
      smartConnectorToNodes({
        steps,
        triggers,
        connections,
        uploads,
      }),
    [steps, triggers, connections, uploads]
  );

  const { nodes: laidOutNodes, ready: layoutReady } = useFlowLayout(
    nodes,
    edges,
    { selectedId: lastPlacingStep?.id }
  );

  const alignedNodes = useMemo(() => {
    const additionalVariablesNode = laidOutNodes.find(({ id }) =>
      id.includes('initialize_additional_variables')
    );

    return laidOutNodes.map((node) => {
      if (node.id.includes('run_get_requests')) {
        return {
          ...node,
          position: {
            x: additionalVariablesNode?.position.x || node.position.x,
            y: node.position.y,
          },
        };
      }

      if (node.id.includes('trigger')) {
        const alignedY =
          additionalVariablesNode?.height && node.height
            ? additionalVariablesNode.position.y +
              (additionalVariablesNode.height - node.height) / 2
            : additionalVariablesNode?.position.y;

        return {
          ...node,
          position: {
            x: node.position.x,
            y: alignedY || node.position.y,
          },
        };
      }

      return node;
    });
  }, [laidOutNodes]);

  const movingRef = useRef(false);

  const onMove = useCallback(() => {
    if (!movingRef.current) {
      // onMoveStart wouldn't reliably fire, so we track
      // moving state locally and check for changes.
      movingRef.current = true;
      setInteracting(true);
    }
  }, [setInteracting]);

  // Debounced to avoid triggering interacting off
  // very often while panning using trackpad, as
  // interacting causes re-renders of each step.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onMoveEnd = useCallback(
    debounce(() => {
      movingRef.current = false;
      setInteracting(false);
    }, 250),
    [setInteracting]
  );

  const scroll = useScrollFlowWhilePlacing();
  const { translateExtent, scrollingX, scrollingY } = useTranslateExtent({
    minZoom: MIN_ZOOM,
    centerNodeId: 'new.trigger',
    canvasPadding: CANVAS_PADDING,
    canvasTopPadding: CANVAS_TOP_PADDING,
    nodes: alignedNodes,
    ready: layoutReady,
  });

  return (
    <StyledFlowWrapper>
      <BackgroundCanvas />
      <ReactFlow
        data-qa="react-flow"
        nodes={alignedNodes}
        edges={edges}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        zoomOnScroll={false}
        panOnScroll={scrollingX || scrollingY}
        paneMoveable={scrollingX || scrollingY}
        translateExtent={translateExtent}
        nodesDraggable={false}
        selectNodesOnDrag={true}
        nodesConnectable={false}
        // This is important so that when you are dragging an element
        // and its placeholder scrolls off the page, the el doesn't disappear
        // mid-drag. When that happens it is actually very awkward
        // because you don't get a final onStop() call by react-draggable
        // (the el disappears so there's nothing to drop!)
        onlyRenderVisibleElements={false}
        onMove={onMove}
        onMoveEnd={onMoveEnd}
        {...nodesAndEdges}
        {...scroll}
      />
    </StyledFlowWrapper>
  );
};
